import React from "react";
import { Link } from "react-router-dom";
import SectionHeading from "./SectionHeading";

/**
 * Senior Advisors — homepage block.
 *
 * Compact 3-column grid (currently 2 named advisors, room to grow). Each
 * card carries portrait + role + career-signal line + LinkedIn + Read
 * Profile link. Deliberately omits a Contact email — Senior Advisors are
 * advisors to the partnership, not the LP's contact point.
 */
const ADVISORS = [
  {
    id: "aw",
    anchor: "alistair-wood",
    name: "Alistair Wood",
    role: "Senior Advisor",
    qualifier: "Corporate Banking & Syndicated Credit",
    short:
      "Former State Manager NSW, HSBC Australia Commercial Banking. Career across UK, Japan and Australia in corporate banking, syndicated credit and risk management at HSBC and Midland Bank.",
    linkedin: "https://www.linkedin.com/in/alistair-wood-345491b/",
    photo: "/portraits/alistair-wood.jpg",
  },
  {
    id: "ab",
    anchor: "angelo-barbati",
    name: "Angelo Barbati",
    role: "Senior Advisor",
    qualifier: "Debt Structuring & Risk",
    short:
      "Former Senior Manager, Middle Market Enterprises, HSBC Bank Australia. Twenty-five years across debt structuring, trade finance, treasury and credit risk at HSBC, ANZ and Banca Monte Paschi.",
    linkedin: "https://www.linkedin.com/in/angelo-barbati-0bb4387a/",
    photo: "/portraits/angelo-barbati.jpg",
  },
];

const AdvisorCard = ({ advisor }) => (
  <article
    id={advisor.anchor}
    data-testid={`kr-senior-advisor-${advisor.id}`}
    className="relative p-7 lg:p-8 border border-[var(--kr-line)] hover:border-[var(--kr-gold)]/60 transition-colors duration-500 group scroll-mt-32"
    style={{ background: "rgba(12, 16, 24, 0.5)" }}
  >
    <div className="flex gap-5 items-start mb-5">
      <div className="relative w-20 h-20 flex-shrink-0 overflow-hidden border border-[var(--kr-line)] bg-[#0c1018]">
        <img
          src={advisor.photo}
          alt={advisor.name}
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            filter:
              "grayscale(40%) contrast(1.05) brightness(0.95) saturate(0.85)",
            objectPosition: "center 20%",
          }}
          loading="lazy"
        />
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,13,18,0) 60%, rgba(10,13,18,0.5) 100%)",
          }}
        />
        <span className="absolute top-1 left-1 w-2.5 h-2.5 border-l border-t border-[var(--kr-gold)]/70" />
        <span className="absolute top-1 right-1 w-2.5 h-2.5 border-r border-t border-[var(--kr-gold)]/70" />
        <span className="absolute bottom-1 left-1 w-2.5 h-2.5 border-l border-b border-[var(--kr-gold)]/70" />
        <span className="absolute bottom-1 right-1 w-2.5 h-2.5 border-r border-b border-[var(--kr-gold)]/70" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-mono-label text-[9px] text-[var(--kr-gold)] mb-2">
          {advisor.role}
        </div>
        <h3 className="font-serif-display text-[var(--kr-text)] text-[1.35rem] leading-[1.15] mb-2">
          {advisor.name}
        </h3>
        <div className="font-serif-display italic text-[var(--kr-text-muted)] text-[12px] leading-[1.4]">
          {advisor.qualifier}
        </div>
      </div>
    </div>

    <div className="kr-rule mb-5" />

    <p className="text-[var(--kr-text)] text-[13.5px] leading-[1.7] mb-6">
      {advisor.short}
    </p>

    <div className="flex flex-wrap gap-3">
      <a
        href={advisor.linkedin}
        target="_blank"
        rel="noreferrer"
        data-testid={`kr-senior-advisor-linkedin-${advisor.id}`}
        className="kr-btn-ghost"
        style={{ padding: "0.7rem 1.25rem", fontSize: "10px" }}
      >
        LinkedIn ↗
      </a>
      <Link
        to={`/advisors#${advisor.anchor}`}
        data-testid={`kr-senior-advisor-profile-${advisor.id}`}
        className="font-mono-label text-[10px] text-[var(--kr-text-muted)] hover:text-[var(--kr-gold)] transition-colors self-center pl-1"
      >
        Read Profile →
      </Link>
    </div>
  </article>
);

const SeniorAdvisors = () => {
  return (
    <section
      id="senior-advisors"
      data-testid="kr-senior-advisors"
      className="relative py-28 lg:py-36 border-t border-[var(--kr-line-soft)]"
    >
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 mb-14">
          <div className="lg:col-span-7">
            <SectionHeading
              index="12"
              label="Senior Advisors"
              title={
                <>
                  Sector and regional depth — engaged{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    non-executively
                  </span>
                  .
                </>
              }
            />
          </div>
          <div className="lg:col-span-5">
            <p className="text-[var(--kr-text-muted)] text-[15.5px] leading-[1.85] lg:pt-8">
              The platform&apos;s Senior Advisors engage with the partnership on
              a non-executive, episodic basis — contributing commercial-banking,
              structuring, credit-risk and life-sciences pattern recognition to
              underwriting and portfolio engagement. They are not partners and
              are not authorised to act on behalf of the firm.
            </p>
          </div>
        </div>

        {/* Tier marker */}
        <div className="flex items-center gap-4 mb-6">
          <span
            aria-hidden
            className="block w-[8px] h-[8px]"
            style={{
              background: "var(--kr-gold)",
              transform: "rotate(45deg)",
            }}
          />
          <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
            Senior Advisors
          </span>
          <span className="flex-1 h-px bg-[var(--kr-line)]" />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {ADVISORS.map((a) => (
            <AdvisorCard key={a.id} advisor={a} />
          ))}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p
            data-testid="kr-senior-advisors-sector-note"
            className="text-[12.5px] text-[var(--kr-text-muted)] leading-[1.6] max-w-[60ch]"
          >
            Life-sciences &amp; pharma advisory is provided by{" "}
            <Link
              to="/regional-coverage#sven-massenhove"
              data-testid="kr-senior-advisors-sven-link"
              className="text-[var(--kr-text)] hover:text-[var(--kr-gold)] transition-colors font-serif-display italic text-[14px]"
            >
              Sven Massenhove
            </Link>
            , who sits under Regional Coverage as Gulf Region Lead.
          </p>
          <Link
            to="/advisors"
            data-testid="kr-senior-advisors-team-link"
            className="kr-link font-mono-label text-[11px] whitespace-nowrap"
          >
            Full Senior Advisor Profiles →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SeniorAdvisors;
